




















































































































/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import { mapState } from 'vuex';
import { HubButton, HubCard, HubIcon } from '@/components/HubComponents';
import {
  gmapsMap as GMaps,
  gmapsPopup as GPopup,
} from 'x5-gmaps';
import statisticsServices from '@/services/hub/statistics';
import VueApexCharts from 'vue-apexcharts';
import { ApexOptions } from 'apexcharts';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { jsPDF as JsPdf } from 'jspdf';

const defaultOptionsDonutCharts = {
  chart: { type: 'donut' },
  legend: { fontSize: '20px', offsetY: 80 },
  plotOptions: {
    pie: {
      customScale: 1.5,
      offsetY: 50,
      donut: {
        labels: {
          show: true,
          name: {
            formatter(value) {
              return value.split(':')[0];
            },
          },
        },
      },
    },
  },
} as ApexOptions;

export default Vue.extend({
  name: 'RealTimeStatistics',

  components: {
    HubButton,
    HubCard,
    HubIcon,
    GMaps,
    GPopup,
    VueApexCharts,
  },

  data() {
    return {
      browserOptions: {
        ...defaultOptionsDonutCharts,
        noData: {
          text: `${this.$t('loading')}...`,
        },
        title: { text: this.$t('browsers') },
      } as ApexOptions,
      browserSeries: [] as number[],
      cdnCrawlerRequestPayload: {} as Services.Statistics.GetRealTime,
      currentType: 'global' as Services.Statistics.Type,
      isLoading: false,
      interval: 0 as unknown as NodeJS.Timeout,
      isThereMoreThanOneCity: false,
      justClickedPreviousLevelButton: false,
      mapMarkers: [] as Components.RealTimeStatistics.MapMarker[],
      mapOptions: {
        center: { lat: 10, lng: 10 },
        mapTypeControl: false,
        streetViewControl: false,
        zoom: 2,
      } as google.maps.MapOptions,
      nextOrPreviousType: {
        city: {
          previous: 'state',
        },
        country: {
          next: 'state',
          previous: 'global',
        },
        global: {
          next: 'country',
        },
        state: {
          next: 'city',
          previous: 'country',
        },
      } as Components.RealTimeStatistics.NextOrPreviousType,
      operationalSystemOptions: {
        ...defaultOptionsDonutCharts,
        noData: {
          text: `${this.$t('loading')}...`,
        },
        title: { text: this.$t('operational_systems') },
      } as ApexOptions,
      operationalSystemSeries: [] as number[],
      platformOptions: {
        ...defaultOptionsDonutCharts,
        noData: {
          text: `${this.$t('loading')}...`,
        },
        title: { text: this.$t('platforms') },
      } as ApexOptions,
      platformSeries: [] as number[],
      totalOnline: 0,
      unknownLocation: 0,
    };
  },

  computed: {
    ...mapState(['events']),
    eventState(): Store.Events.Event {
      return this.events.event;
    },
  },

  watch: {
    async currentType(): Promise<void> {
      this.cdnCrawlerRequestPayload = {
        ...this.cdnCrawlerRequestPayload,
        type: this.currentType,
      };

      if (this.justClickedPreviousLevelButton) {
        this.isLoading = true;

        const response = await this.getFullStatistics(this.cdnCrawlerRequestPayload);

        this.setFullStatistics(response);

        this.isLoading = false;

        this.mapOptions = {
          ...this.mapOptions,
          zoom: (this.mapOptions.zoom as number) - 1,
        };
      }

      this.justClickedPreviousLevelButton = false;
    },
  },

  created() {
    // Adding the listener for the "onClickOnDataDownload__real_time" event
    // fired by the parent (ViewStatistics.vue)
    this.$parent.$on('onClickOnDataDownload__real_time', this.onClickOnDataDownload);
  },

  beforeDestroy() {
    // Removing the listener for the "onClickOnDataDownload__real_time" event
    // fired by the parent (ViewStatistics.vue)
    this.$parent.$off('onClickOnDataDownload__real_time');
    // Removing the created interval for cdn-crawler requests
    clearInterval(this.interval);
  },

  async mounted() {
    this.isLoading = true;

    this.cdnCrawlerRequestPayload = {
      customerPlansId: this.eventState.customer_plans_id,
      eventHash: this.eventState.event_hash,
      type: this.currentType,
    };

    const response = await this.getFullStatistics(this.cdnCrawlerRequestPayload);

    this.setFullStatistics(response);
    this.isLoading = false;

    this.interval = setInterval(async () => {
      this.setFullStatistics(
        await this.getFullStatistics(this.cdnCrawlerRequestPayload),
      );
    }, 15000);
  },

  methods: {
    firstCapitalLetter(value: string): string {
      return `${value.slice(0, 1).toUpperCase()}${value.slice(1).toLowerCase()}`;
    },
    formatValue(value: number): string {
      return new Intl.NumberFormat().format(value);
    },
    async getFullStatistics(
      payload: Services.Statistics.GetRealTime,
    ): Promise<Components.RealTimeStatistics.GetFullStatistics> {
      const response = await this.getRealTime(payload);

      return {
        browser: response[3],
        os: response[2],
        platform: response[1],
        region: response[0],
      };
    },
    async getRealTime(
      payload: Services.Statistics.GetRealTime,
    ): Promise<Services.Statistics.Response.RealTimeView> {
      return statisticsServices.getRealTime(payload)
        .then(({ data }) => data.views)
        .catch((error) => {
          console.error('RealTimeStatistics - method: "getRealTime()" ', error);

          this.showNotification('error_fetching_statistical_data', 'error');

          throw new Error(error);
        });
    },
    async onClickMapMarker(marker: Components.RealTimeStatistics.MapMarker): Promise<void> {
      this.isLoading = true;

      if (this.currentType === 'city') {
        this.isLoading = false;
        return;
      }

      this.currentType = this.nextOrPreviousType[this.currentType].next;

      switch (this.currentType) {
        case 'city':
          if (!this.isThereMoreThanOneCity) {
            this.isLoading = false;
            this.currentType = this.nextOrPreviousType.city.previous;
            return;
          }

          this.mapOptions = {
            center: marker.position,
            zoom: 7,
          };

          this.cdnCrawlerRequestPayload = {
            ...this.cdnCrawlerRequestPayload,
            city: marker.label,
            type: this.currentType,
          };
          break;
        case 'country':
          this.mapOptions = {
            center: marker.position,
            zoom: 4,
          };

          this.cdnCrawlerRequestPayload = {
            ...this.cdnCrawlerRequestPayload,
            country: marker.label,
            type: this.currentType,
          };
          break;
        case 'state':
          this.mapOptions = {
            center: marker.position,
            zoom: 6,
          };

          this.cdnCrawlerRequestPayload = {
            ...this.cdnCrawlerRequestPayload,
            state: marker.label,
            type: this.currentType,
          };
          break;
        default: break;
      }

      const response = await this.getFullStatistics(this.cdnCrawlerRequestPayload);

      this.setFullStatistics(response);
      this.isLoading = false;

      if (this.currentType === 'state') {
        this.isThereMoreThanOneCity = response.region.length > 1;
      }
    },
    async onClickOnDataDownload(
      exportFormat: Views.ViewStatistics.ExportFormat,
    ): Promise<void> {
      const filename = this.$t('real_time_statistics').toString().replace(/\s/g, '_').toLowerCase();

      const canvasElement = await html2canvas(
        this.$el.querySelector('#real-time-statistics__print') as HTMLElement,
        {
          useCORS: true,
          allowTaint: false,
          scale: 2,
          ignoreElements: (node) => (
            node.classList.value.includes('previous-level-button')
            || node.classList.value.includes('gm-control-active')
            || node.classList.value.includes('gmnoprint')
          ),
        },
      ).catch((error) => console.error('Error converting HTML to canvas element', error));

      if (!(canvasElement instanceof HTMLCanvasElement)) return;

      switch (exportFormat) {
        case 'png': {
          const url = canvasElement.toDataURL('image/png');

          saveAs(url, `${filename}.png`);
          window.URL.revokeObjectURL(url);
          break;
        }
        case 'pdf': {
          const pdf = new JsPdf();
          const height = pdf.internal.pageSize.getHeight() * 0.98;
          const width = pdf.internal.pageSize.getWidth() * 0.9;

          pdf.addImage(canvasElement, 10, 5, width, height);
          pdf.save(filename);
          break;
        }
        default: break;
      }

      this.$emit('exported');
    },
    resetConfig(): void {
      this.currentType = 'global';

      this.mapOptions = {
        center: { lat: 10, lng: 10 },
        mapTypeControl: false,
        streetViewControl: false,
        zoom: 2,
      };
    },
    setFullStatistics(data: Components.RealTimeStatistics.GetFullStatistics): void {
      if (!data.region.length) this.resetConfig();

      this.setRegion(data.region);
      this.setPlatform(data.platform);
      this.setOperationalSystems(data.os);
      this.setBrowser(data.browser);
    },
    setBrowser(data: Services.Statistics.Response.BrowserRealTime[]): void {
      this.browserOptions = {
        ...this.browserOptions,
        labels: data.map((browser) => `${browser._id.browser}: ${browser.count}`),
      };

      this.browserSeries = data.map((browser) => browser.count);
    },
    setOperationalSystems(data: Services.Statistics.Response.OperationalSystemRealTime[]): void {
      this.operationalSystemOptions = {
        ...this.operationalSystemOptions,
        labels: data.map((os) => `${os._id.os}: ${os.count}`),
      };

      this.operationalSystemSeries = data.map((os) => os.count);
    },
    setPlatform(data: Services.Statistics.Response.PlatformRealTime[]): void {
      this.platformOptions = {
        ...this.platformOptions,
        labels: data.map(
          (platform) => `${this.firstCapitalLetter(platform._id.platform)}: ${platform.count}`,
        ),
      };

      this.platformSeries = data.map((platform) => platform.count);
    },
    setRegion(data: Services.Statistics.Response.RegionRealTime[]): void {
      this.mapMarkers = [];
      this.totalOnline = 0;
      this.unknownLocation = 0;

      data.forEach((region) => {
        const label = (region._id.country || region._id.state || region._id.city) as string;

        this.totalOnline += region.count;

        if (label.toLowerCase() === 'unknown') {
          this.unknownLocation += region.count;
          return;
        }

        this.mapMarkers.push({
          label,
          position: {
            lat: parseFloat(`${region.latitude}`),
            lng: parseFloat(`${region.longitude}`),
          },
          value: region.count,
        });
      });
    },
    showNotification(text: string, type: 'error' | 'success'): void {
      this.$store.dispatch('notification/create', { text, type });
    },
  },
});
